import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { 
  MessageSquareQuote,
  Star,
  Loader2,
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Image as ImageIcon
} from 'lucide-react';

// Interactive Star Rating Component
function StarRatingInput({ rating, setRating }) {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
          data-testid={`star-${star}`}
        >
          <Star 
            className={`w-8 h-8 transition-colors ${
              star <= (hoverRating || rating) 
                ? 'text-amber-400 fill-amber-400' 
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function TestimonialSubmit() {
  const { token } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [requestData, setRequestData] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  useEffect(() => {
    fetchRequestData();
  }, [token]);
  
  const fetchRequestData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/testimonials/submit/${token}`);
      setRequestData(response.data);
      
      // Pre-fill if editing
      if (response.data.existingRating) {
        setRating(response.data.existingRating);
      }
      if (response.data.existingText) {
        setText(response.data.existingText);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired link';
      setError(message);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setPhotoPreview(event.target.result);
    };
    reader.readAsDataURL(file);
    
    // Store file
    setPhoto(file);
  };
  
  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (text.trim().length < 20) {
      toast.error('Please write at least 20 characters');
      return;
    }
    
    if (!permissionGranted) {
      toast.error('Please grant permission to use your testimonial');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Prepare photo data if exists
      let photoBase64 = null;
      let photoMimeType = null;
      
      if (photo) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
        });
        reader.readAsDataURL(photo);
        photoBase64 = await base64Promise;
        photoMimeType = photo.type;
      }
      
      await axios.post(`/api/testimonials/submit/${token}`, {
        rating,
        text: text.trim(),
        permissionGranted,
        photoBase64,
        photoMimeType
      });
      
      setSubmitted(true);
      toast.success('Thank you for your testimonial!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit testimonial');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Unable to Load</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-serif font-semibold mb-3">Thank You!</h2>
            <p className="text-muted-foreground mb-6">
              Your testimonial has been submitted successfully. We truly appreciate you taking the time to share your experience with PATH.
            </p>
            <Button onClick={() => navigate('/')}>
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4" data-testid="testimonial-submit-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">
            Share Your PATH Experience
          </h1>
          <p className="text-muted-foreground">
            Hi {requestData?.firstName}, we&apos;d love to hear about your journey
          </p>
        </div>
        
        {/* Edit Request Banner */}
        {requestData?.editRequestMessage && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-orange-800 mb-1">Edit Requested</h3>
                  <p className="text-sm text-orange-700">{requestData.editRequestMessage}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquareQuote className="w-5 h-5 text-primary" />
              Your Testimonial
            </CardTitle>
            <CardDescription>
              Your honest feedback helps others discover the value of PATH
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div>
                <Label className="text-base font-medium mb-3 block">
                  How would you rate your PATH experience? *
                </Label>
                <StarRatingInput rating={rating} setRating={setRating} />
                {rating > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {rating === 5 && "Excellent! We're thrilled you had such a great experience."}
                    {rating === 4 && "Great! We're glad you found value in PATH."}
                    {rating === 3 && "Good. We appreciate your feedback."}
                    {rating === 2 && "We appreciate your honesty and will work to improve."}
                    {rating === 1 && "We're sorry to hear that. Your feedback helps us improve."}
                  </p>
                )}
              </div>
              
              {/* Testimonial Text */}
              <div>
                <Label className="text-base font-medium mb-3 block">
                  Share your experience *
                </Label>
                <Textarea
                  placeholder="Tell us about your journey with PATH. What insights did you gain? How has it impacted your leadership approach?"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={6}
                  className="resize-none"
                  data-testid="testimonial-text"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  {text.length} / 20 minimum characters
                </p>
              </div>
              
              {/* Photo Upload */}
              <div>
                <Label className="text-base font-medium mb-3 block">
                  Add a photo <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                
                {photoPreview ? (
                  <div className="relative inline-block">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-32 h-32 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/90"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  >
                    <ImageIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-1">
                      Click to upload a photo
                    </p>
                    <p className="text-xs text-muted-foreground">
                      JPG, PNG up to 5MB
                    </p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  data-testid="photo-input"
                />
              </div>
              
              {/* Permission Checkbox */}
              <div 
                className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={() => setPermissionGranted(!permissionGranted)}
              >
                <Checkbox
                  id="permission"
                  checked={permissionGranted}
                  onCheckedChange={setPermissionGranted}
                  className="mt-0.5 pointer-events-none"
                  data-testid="permission-checkbox"
                />
                <div>
                  <Label htmlFor="permission" className="font-medium cursor-pointer">
                    I grant permission to use my testimonial *
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    I agree that Secret Sauce may use my testimonial, name, and photo (if provided) 
                    on their website, marketing materials, and social media.
                  </p>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button 
                type="submit" 
                size="lg" 
                className="w-full"
                disabled={submitting}
                data-testid="submit-testimonial-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MessageSquareQuote className="w-4 h-4 mr-2" />
                    Submit Testimonial
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Your feedback is valuable to us. Thank you for being part of the PATH community.
        </p>
      </div>
    </div>
  );
}
